import { useState, useCallback } from 'react';
import { useIntl } from 'react-intl';
import moment from 'moment';
import {
  OutlierCategory,
  FilterValues,
  OutlierType,
  UniversalFilterValue,
  FilterKeyType,
  FilterKey,
} from '../types/types';
import { ContactStatusOptions } from '@amzn/aet-contactrecommendation-kit';
import { OutlierTimeOption } from '@amzn/aet-dynamo-kit';

interface OutlierFiltersProps {
  initiaViewDataBy: OutlierTimeOption;
  initialTimeOption: string;
  initialTimeOptions: string[];
  categories: OutlierType[];
  associates: string[];
  tmList: string[];
  initialStatus: ContactStatusOptions;
  isOMException?: boolean;
}

export const useOutlierFilters = ({
  initiaViewDataBy,
  initialTimeOption,
  initialTimeOptions,
  categories,
  associates,
  tmList,
  initialStatus,
  isOMException = false,
}: OutlierFiltersProps) => {
  const [filters, setFilters] = useState<FilterValues>({
    viewDataBy: initiaViewDataBy,
    timeOption: initialTimeOption,
    timeOptions: initialTimeOptions,
    tm: UniversalFilterValue.ALL,
    category: UniversalFilterValue.ALL,
    associate: UniversalFilterValue.ALL,
    status: initialStatus,
  });

  const updateFilters = useCallback((newFilters: Partial<FilterValues>) => {
    setFilters(current => ({
      ...current,
      ...newFilters,
    }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({
      viewDataBy: initiaViewDataBy,
      timeOptions: [initialTimeOption],
      timeOption: initialTimeOption,
      category: UniversalFilterValue.ALL,
      associate: UniversalFilterValue.ALL,
      tm: UniversalFilterValue.ALL,
      status: initialStatus,
    });
  }, [initialTimeOption, initialStatus]);

  const removeFilter = useCallback(
    (filterKey: FilterKeyType) => {
      updateFilters({
        [filterKey]:
          filterKey === FilterKey.TIME_OPTION
            ? initialTimeOption
            : UniversalFilterValue.ALL,
      });
    },
    [updateFilters, initialTimeOption]
  );

  const filterCategories = useCallback(
    (categories: OutlierCategory[]): OutlierCategory[] => {
      return categories
        .map(category => ({
          ...category,
          contacts: category.contacts.filter(contact => {
            const matchesStatus =
              filters.status === UniversalFilterValue.ALL ||
              contact.status === filters.status;
            const matchesCategory =
              filters.category === UniversalFilterValue.ALL ||
              category.title === filters.category;

            const hasAssociateFilterApplied =
              filters.associate !== UniversalFilterValue.ALL;

            const hasTMFilterApplied = filters.tm !== UniversalFilterValue.ALL;

            const hasAllUserSelected =
              filters.associate === UniversalFilterValue.ALL ||
              filters.tm === UniversalFilterValue.ALL;

            const matchesAssociate = contact.user.login === filters.associate;
            const matchesTM = contact.tmAlias === filters.tm;

            const matchesUser = hasAssociateFilterApplied
              ? matchesAssociate
              : hasTMFilterApplied
                ? matchesTM
                : hasAllUserSelected;

            return (
              (isOMException || matchesStatus) && matchesCategory && matchesUser
            );
          }),
        }))
        .filter(category => category.contacts.length > 0);
    },
    [filters]
  );

  const intl = useIntl();

  const getOMOutlierFilterInfoStripText = useCallback((): string => {
    const isWeeklyPeriodSelected =
      filters.viewDataBy == OutlierTimeOption.WEEKLY;
    const isTMSelected = filters.tm && filters.tm !== UniversalFilterValue.ALL;
    const isCategorySelected =
      filters.category && filters.category !== UniversalFilterValue.ALL;

    const tmText = isTMSelected ? `@${filters.tm}` : 'all TMs';

    const categoryText = isCategorySelected
      ? `"${filters.category}"`
      : 'all categories';

    if (isWeeklyPeriodSelected) {
      const [year, week] = filters.timeOption.split('-');

      const weekStart = moment()
        .year(parseInt(year))
        .week(parseInt(week))
        .startOf('week');

      return intl.formatMessage(
        {
          id: 'cs_perfportal_om_outlier_filter_info_strip_weekly_selected',
          defaultMessage: `Results for week of {month} {day} (Week {year}-{week})`,
        },
        {
          tmText,
          categoryText,
          month: weekStart.format('MMMM'),
          day: weekStart.format('D'),
          year,
          week,
        }
      );
    } else {
      return intl.formatMessage(
        {
          id: 'cs_perfportal_om_outlier_filter_viewing_outlier_reviews_for_tm_on_category',
          defaultMessage: `Viewing outlier reviews of {tmText} on "{category}" for the selected dates`,
        },
        { tmText, category: filters.category }
      );
    }
  }, [filters]);

  return {
    filters,
    updateFilters,
    clearFilters,
    removeFilter,
    filterCategories,
    getOMOutlierFilterInfoStripText,
  };
};
